import { NotesSection, Song, SongTrack } from '~/interfaces';

import clearString from '../../utils/clear-string';
import { generatePlayerChangesForTrack } from './generate-player-changes';
import isNotesSection from './is-notes-section';
import { getLastNoteEnd } from './notes-selectors';

const isBetween = (start: number, end: number, value: number) => value >= start && value <= end;

const notesLyricsLength = (section: NotesSection) => section.notes.map((n) => n.lyrics).join('');

/// unique characters in string
const uniqueCharactersCount = (str: string) => {
  const uniqueChars = new Set(clearString(str));
  return uniqueChars.size;
};

const sectionHasMoreLyrics = (sections: NotesSection[], section: NotesSection) => {
  const sectionsLyrics = sections.reduce((acc, s) => `${acc}${notesLyricsLength(s)}`, '');
  const lyrics = notesLyricsLength(section);

  return uniqueCharactersCount(sectionsLyrics) / uniqueCharactersCount(lyrics) < 0.5;
};

const isSectionOverlapping = (sections: NotesSection[], section: NotesSection) => {
  const sectionStart = section.start;
  const sectionEnd = getLastNoteEnd(section);

  const result = sections.filter((s) => {
    const start = s.start;
    const end = getLastNoteEnd(s);
    return (
      isBetween(sectionStart, sectionEnd, start) ||
      isBetween(sectionStart, sectionEnd, end) ||
      (start <= sectionStart && end >= sectionEnd)
    );
  });

  const moreLyrics = sectionHasMoreLyrics(result, section);

  return {
    section,
    shouldReplace: result.length === 0 || moreLyrics,
    sectionsToRemove: result,
  };
};

export default function mergeTracks(tracks: Song['tracks'], song: Song) {
  if (!tracks) {
    return tracks;
  } else if (tracks.length < 2) {
    // One track is already its own merge, and none has nothing to merge - reading `notesSections[1]`
    // below threw outright on a song with an empty track list.
    return tracks[0];
  }
  const notesSections = tracks.map((track) => track.sections.filter(isNotesSection));

  const notOverlappingSections = notesSections[1].map((section) => isSectionOverlapping(notesSections[0], section));

  let finalSections = [...notesSections[0]];

  notOverlappingSections.forEach((section) => {
    if (section.shouldReplace) {
      finalSections = finalSections.filter(
        (s) => !section.sectionsToRemove.some((removedSection) => removedSection.start === s.start),
      );
      finalSections.push(section.section);
    }
  });

  const finalTrack: SongTrack = {
    ...tracks[0],
    sections: finalSections.sort((a, b) => a.start - b.start),
    changes: [],
  };

  finalTrack.changes = generatePlayerChangesForTrack(finalTrack, song);

  return finalTrack;
}
